import { useEffect, useState } from 'react';
import { useEditMode, useProperty } from '@vibesites/runtime/react';
import { clean } from './quinn';

type AnyRecord = Record<string, unknown>;

/** Untyped property access for fields the Property type doesn't declare (custom_*, units, …). */
export function useRawProperty(): AnyRecord {
  return (useProperty() as unknown as AnyRecord) || {};
}

/** Custom fields arrive flattened (`custom_*` keys) on deployed sites and as a `custom_fields` array in raw snapshots. */
export function useCustomField(id: string): string | null {
  const p = useRawProperty();
  const flat = clean(p[id]);
  if (flat) return flat;
  const list = (p.custom_fields as Array<{ ID?: string; value?: unknown }> | undefined) || [];
  return clean(list.find((f) => f.ID === id)?.value);
}

export function useReducedMotion(): boolean {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const apply = () => setReduced(mq.matches);
    apply();
    mq.addEventListener('change', apply);
    return () => mq.removeEventListener('change', apply);
  }, []);
  return reduced;
}

export function useIsEditing(): boolean {
  return !!useEditMode()?.isEditMode;
}

/**
 * Scroll reveal for every [data-reveal] on the page: fade + rise once, as the element enters the viewport.
 * Never runs in the editor, and elements already on screen when it starts are shown immediately.
 */
export function useScrollReveal(): void {
  const editing = useIsEditing();
  useEffect(() => {
    const root = document.documentElement;
    if (editing || !('IntersectionObserver' in window)) {
      root.classList.remove('q-reveal');
      return;
    }
    const inView = (el: Element) => {
      const r = el.getBoundingClientRect();
      return r.top < window.innerHeight * 0.92 && r.bottom > 0;
    };
    const io = new IntersectionObserver(
      (entries) =>
        entries.forEach((en) => {
          if (en.isIntersecting) {
            en.target.classList.add('is-in');
            io.unobserve(en.target);
          }
        }),
      { rootMargin: '0px 0px -8% 0px', threshold: 0.05 },
    );
    const track = (scope: ParentNode) =>
      scope.querySelectorAll('[data-reveal]:not(.is-in)').forEach((el) => {
        if (inView(el)) el.classList.add('is-in');
        else io.observe(el);
      });
    track(document);
    root.classList.add('q-reveal');
    const mo = new MutationObserver((muts) => muts.forEach((m) => m.addedNodes.forEach((n) => n instanceof Element && track(n.parentNode || n))));
    mo.observe(document.body, { childList: true, subtree: true });
    return () => {
      io.disconnect();
      mo.disconnect();
      root.classList.remove('q-reveal');
    };
  }, [editing]);
}

/** Header state: scrolled past the offer banner (when shown) plus `offset`, and whether the user is scrolling down. */
export function useScrollState(offset = 24): { scrolled: boolean; down: boolean } {
  const [state, setState] = useState({ scrolled: false, down: false });
  useEffect(() => {
    let lastY = window.scrollY;
    const onScroll = () => {
      const y = window.scrollY;
      const bannerH = (document.querySelector('.offer-banner') as HTMLElement | null)?.offsetHeight || 0;
      setState({ scrolled: y > bannerH + offset, down: y > lastY && y > 200 });
      lastY = y;
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [offset]);
  return state;
}

export function useMediaQuery(query: string): boolean {
  const [match, setMatch] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia(query);
    const apply = () => setMatch(mq.matches);
    apply();
    mq.addEventListener('change', apply);
    return () => mq.removeEventListener('change', apply);
  }, [query]);
  return match;
}
